import { useEffect, useState } from "react";
import { DeleteOutlined, ReloadOutlined } from "@ant-design/icons";
import { serverFetch } from "@server";
import { Button, Checkbox, Empty, Input, message, Popconfirm, Space, Spin, Table } from "antd";
import { renderCheckbox, renderDateTime, renderNumber } from "@extensions/helper";
import { refreshButton } from "@extensions/design";
import { useViewportBreakpoint } from "../../hooks/useViewportBreakpoint";
import CompanyBase from "./company-base";

const CompanyBaseCard = ({ record, selectedIds, onToggleSelect, onOpen }) => {
  const checked = selectedIds.includes(record.id);
  const dateStr = renderDateTime(record.datetimeReg);
  const guidStr = record.guid != null && record.guid !== "" ? `${record.guid}` : "";

  return (
    <div
      className={`company-bases-card${record.isDel ? " company-bases-card--deleted" : ""}`}
      onClick={e => {
        if (e.target.closest(".ant-checkbox-wrapper") || e.target.closest("input")) return;
        onOpen(record.id);
      }}
    >
      <Checkbox
        checked={checked}
        onChange={e => {
          e.stopPropagation();
          onToggleSelect(record.id, e.target.checked);
        }}
        onClick={e => e.stopPropagation()}
      />
      <div className="company-bases-card-body">
        <div className="company-bases-card-title">{record.name ?? ""}</div>
        <div className="company-bases-card-meta">
          <span className="company-bases-card-id"># {record.id}</span>
          <span className="company-bases-card-date">{dateStr}</span>
        </div>
        {guidStr ? <div className="company-bases-card-guid">{guidStr}</div> : null}
      </div>
    </div>
  );
};

const CompanyBases = () => {
  const viewport = useViewportBreakpoint();
  const isMobile = viewport === "mobile";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({ showDelete: false });
  const [searchStr, setSearchStr] = useState('');
  const [selected, setSelected] = useState();
  const [selectedIds, setSelectedIds] = useState([]);

  const getData = () => {
    setLoading(true);
    serverFetch(`db?showDeleted=${filter.showDelete}`)
      .then(data => {
        setLoading(false);
        setItems(data);
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage);
      })
  }

  useEffect(getData, [filter]);

  const columns = [
    { dataIndex: "id", title: "#" },
    { dataIndex: "isDel", title: "Удалена", render: renderCheckbox },
    { dataIndex: "name", title: "Наименование" },
    { dataIndex: "guid", title: "Код" },
    { dataIndex: "nstrVer", title: "Настройки", render: renderNumber },
    { dataIndex: "datetimeReg", title: "Регистрация", render: renderDateTime },
  ]

  const filtered = items.filter(i => {
    if (!searchStr) return true;
    if (i.name.toLowerCase().includes(searchStr.toLowerCase())) return true;
    return false;
  })

  const onFilterChange = value => setFilter({ ...filter, ...value });
  const onCloseEdit = refresh => {
    setSelected();
    refresh && getData();
  }

  const handleDelete = () => {
    if (!selectedIds.length) return;
    
    setLoading(true);
    serverFetch("db", { method: "DELETE", bodyData: selectedIds })
      .then(() => {
        message.success("Базы удалены");
        setSelectedIds([]);
        getData();
      })
      .catch(e => {
        setLoading(false);
        message.error(e.userMessage || "Ошибка при удалении");
      });
  };

  const toggleRowSelect = (id, checked) => {
    setSelectedIds(prev =>
      checked ? [...prev, id] : prev.filter(x => x !== id)
    );
  };

  const searchInput = (
    <Input
      className="company-bases-search"
      placeholder="Поиск"
      value={searchStr}
      onChange={e => setSearchStr(e.target.value)}
    />
  );

  const showDeletedCheckbox = (
    <Checkbox checked={filter.showDelete} onChange={e => onFilterChange({ showDelete: e.target.checked })}>
      Показать удаленные
    </Checkbox>
  );

  const refreshEl = isMobile ? (
    <Button className="misoft-btn" type="default" icon={<ReloadOutlined />} onClick={getData} />
  ) : (
    refreshButton(getData)
  );

  const deleteConfirm = (
    <Popconfirm
      title="Подтверждение удаления"
      description={`Вы уверены, что хотите удалить ${selectedIds.length} баз(у)?`}
      onConfirm={handleDelete}
      okText="Удалить"
      cancelText="Отмена"
      disabled={!selectedIds.length}
    >
      {isMobile ? (
        <span className="company-bases-icon-btn-wrap">
          <Button danger disabled={!selectedIds.length || loading} icon={<DeleteOutlined />} />
        </span>
      ) : (
        <Button danger disabled={!selectedIds.length || loading}>
          Удалить
        </Button>
      )}
    </Popconfirm>
  );

  return (
    <div className="company-bases">
      {isMobile ? (
        <div className="company-bases-toolbar company-bases-toolbar--mobile">
          {searchInput}
          <div className="company-bases-toolbar-actions">
            {showDeletedCheckbox}
            <Space size="small" className="company-bases-toolbar-icon-actions">
              {refreshEl}
              {deleteConfirm}
            </Space>
          </div>
        </div>
      ) : (
        <Space>
          {showDeletedCheckbox}
          {searchInput}
          {refreshEl}
          {deleteConfirm}
        </Space>
      )}
      {isMobile ? (
        <div className="company-bases-mobile-body">
          <Spin spinning={loading}>
            {!loading && filtered.length === 0 ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет баз" />
            ) : filtered.length > 0 ? (
              <div className="company-bases-list">
                {filtered.map(record => (
                  <CompanyBaseCard
                    key={record.id}
                    record={record}
                    selectedIds={selectedIds}
                    onToggleSelect={toggleRowSelect}
                    onOpen={id => setSelected(id)}
                  />
                ))}
              </div>
            ) : null}
          </Spin>
        </div>
      ) : (
        <Table
          dataSource={filtered}
          loading={loading}
          columns={columns}
          pagination={false}
          size="small"
          rowKey="id"
          rowSelection={{
            type: "checkbox",
            selectedRowKeys: selectedIds,
            onChange: selectedRowKeys => setSelectedIds(selectedRowKeys),
            renderCell: (checked, record, index, node) => node,
          }}
          onRow={record => ({
            onClick: e => {
              if (e.target.type !== "checkbox" && e.target.closest(".ant-checkbox") === null) {
                setSelected(record.id);
              }
            },
          })}
        />
      )}
      {selected && <CompanyBase onClose={onCloseEdit} id={selected} />}
    </div>
  );
};

export default CompanyBases;